/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { MonitorSmartphone, Mail, MessageCircle, BookOpen, LifeBuoy, Shield } from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import PageBottomCTA from "@/components/landing/PageBottomCTA";
import PageMeta from "@/components/PageMeta";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CARDS = [
  { icon: MessageCircle, title: "Product questions", body: "Tell us what you are trying to achieve and we will point you in the right direction.", action: "Contact the team", href: "/contact", testId: "support-whatsapp-card" },
  { icon: Mail, title: "Email support", body: "Send the account, workflow, or billing context and our team can investigate from the right starting point.", action: "support@shopbook.tech", href: "mailto:support@shopbook.tech", testId: "support-email-card" },
  { icon: BookOpen, title: "Workflow guidance", body: "Get help with sales, inventory, customer credit, staff access and more.", action: "Browse answers", href: "#faq", testId: "support-articles-card" },
];

const FAQS = [
  { q: "Which work can continue offline?", a: "Core mobile sales, stock, product, and customer-credit operations use local-first storage and can queue for synchronization when connectivity returns. Repeated operations are protected by idempotency controls, and conflicts are surfaced for review rather than silently applied." },
  { q: "How do I add staff members?", a: "Open More → Staff Access. Tap Add Staff, enter a name and a phone number, choose a role (Cashier, Manager or Owner), and Shopbook generates a PIN you can share securely. You can suspend or remove staff at any time." },
  { q: "Can I switch between branches?", a: "Yes. Tap the branch name on top of any screen to switch. You can view per-branch stock, sales and debt, or open the combined dashboard for a roll-up across your whole business." },
  { q: "How do I record a credit sale?", a: "After adding items to the cart, tap Sold on credit instead of Complete sale. Enter the customer's name (and phone if you want) and the amount they still owe. The sale appears in your Debt Tracker until it's paid." },
  { q: "What should I do if my phone is lost or stolen?", a: "Contact support immediately and secure access to the registered phone number and email account. Shopbook protects sessions with authenticated access, PIN and OTP controls; synchronized workspace records remain available after you sign in on an authorized replacement device." },
  { q: "Does Shopbook charge fees per transaction?", a: "Shopbook plans use a fixed subscription price rather than taking a percentage of each sale. Choose the plan capacity that matches your branches, team members and products." },
  { q: "What uses AI credits?", a: "Assistant messages use 0.5 credit and product-image generation uses 5 credits. Plans include monthly credits, and optional top-ups are available. Failed AI operations are not charged. WhatsApp and Telegram alerts are coming soon and will show their credit price before activation." },
  { q: "Can I export sales history?", a: "Yes. Authorized users can filter sales history by cashier, payment status, and date range, then export the filtered history as CSV." },
  { q: "Which languages does the app support?", a: "English, Arabic, French, Hausa, Igbo, Portuguese, Swahili, Yoruba and Zulu. You can switch the app language and the assistant language independently." },
  { q: "How do I delete my account?", a: (
    <>
      Owners can start deletion from Settings on web or mobile. You can also make a verified request from{" "}
      <Link to="/account-deletion" className="text-[#0E6B6B] underline">our account deletion page</Link>.
    </>
  ) },
  { q: "Is my data sold or used to train AI?", a: "No. Your business data is yours. We use it to power Shopbook features for you. We do not sell it, and we do not use it to train third-party AI models. See our Privacy Policy for details." },
];

export default function Support() {
  return (
    <>
      <PageMeta title="Support" description="Get practical help with Shopbook accounts, sales, stock, customer credit, staff access, branches, billing, and data controls." />
      <PageHero
        eyebrow="Support center"
        title="Start with the exact problem."
        italicTail="We’ll help from there."
        intro="Share the account, screen, action, and result you expected. Clear context helps the product team give you a useful answer faster."
      />

      {/* Contact cards */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {CARDS.map((c) => (
            <article
              key={c.title}
              data-testid={c.testId}
              className="rounded-3xl bg-[#F7FAF9] p-7 border border-[#0E6B6B]/10 flex flex-col"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#0E6B6B] text-white flex items-center justify-center mb-5">
                <c.icon className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-xl font-semibold text-[#0A2424]">{c.title}</h3>
              <p className="mt-2 text-[15px] text-[#4B5E5E] flex-1">{c.body}</p>
              <a
                href={c.href}
                className="mt-5 inline-flex items-center font-semibold text-[#0E6B6B] hover:text-[#138282]"
              >
                {c.action} →
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* Full FAQ */}
      <section id="faq" className="bg-[#F7FAF9] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D96C4A]">FAQ</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05] text-[#0A2424]">
              Quick answers to common questions.
            </h2>
            <p className="mt-5 text-[#4B5E5E]">Can't find what you need? Reach out and we'll help.</p>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="border-b border-[#0E6B6B]/12"
                  data-testid={`support-faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-display text-lg md:text-xl text-[#0A2424] hover:text-[#0E6B6B] py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] md:text-base text-[#4B5E5E] leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-white py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid sm:grid-cols-3 gap-6">
          {[
            { icon: MonitorSmartphone, label: "Guidance for web and mobile" },
            { icon: LifeBuoy, label: "Support from a real product team" },
            { icon: Shield, label: "Business data is not sold" },
          ].map((i) => (
            <div key={i.label} className="flex items-center gap-3 text-[15px] text-[#1c2c2c]">
              <i.icon className="h-5 w-5 text-[#0E6B6B]" />
              {i.label}
            </div>
          ))}
        </div>
      </section>

      <PageBottomCTA
        eyebrow="Still need help?"
        title="Talk to a real person."
        body="Send us a message with the workflow, account, or billing question you need help with."
        primaryLabel="Contact us"
        primaryTo="/contact"
      />
    </>
  );
}
